import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Users,
  Search,
  Filter,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Eye,
  MoreVertical,
  Trash2,
  Plus,
  Bell,
  Mail,
  RefreshCw,
  Target,
  Users2,
  Briefcase,
  MessageSquare,
  CheckCircle,
  FileText,
  UserCheck,
  X,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Phone,
  Hash,
  Award,
  Building2,
  User,
  AlertCircle,
  Star,
  Edit,
  Send,
  BarChart2
} from 'lucide-react';
import EnhancedReminderPanel from './EnhancedReminderPanel';
import EnhancedPersonalNotesModal from './EnhancedPersonalNotesModal';
import UnifiedCandidateModal from './UnifiedCandidateModal';
import CertificationIcon from './CertificationIcon';

const CandidatesTab = ({
  // Data props
  candidates,
  filteredCandidates,
  paginatedCandidates,
  currentPage,
  totalPages,
  itemsPerPage,
  dataLoading,
  currentUser,
  
  // Search and filter states
  searchTerm,
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  showAdvancedFilters,
  setShowAdvancedFilters,
  
  // Advanced filter states
  clientFilter,
  setClientFilter,
  buHeadFilter,
  setBuHeadFilter,
  skillsFilter,
  setSkillsFilter,
  locationFilter,
  setLocationFilter,
  contractTypeFilter,
  setContractTypeFilter,
  rateRangeFilter,
  setRateRangeFilter,
  marginFilter,
  setMarginFilter,
  reportingStatusFilter,
  setReportingStatusFilter,
  projectStatusFilter,
  setProjectStatusFilter,
  endDateRangeFilter,
  setEndDateRangeFilter,
  customEndDateStart,
  setCustomEndDateStart,
  customEndDateEnd,
  setCustomEndDateEnd,
  clientProjectManagerFilter,
  setClientProjectManagerFilter,
  clientResourceManagerFilter,
  setClientResourceManagerFilter,
  reportingManagerFilter,
  setReportingManagerFilter,
  extensionStatusFilter,
  setExtensionStatusFilter,
  startDateFilter,
  setStartDateFilter,
  endDateFilter,
  setEndDateFilter,
  showRecentOnboarded,
  setShowRecentOnboarded,
  certificationFilter,
  setCertificationFilter,
  uniqueCertifications,

  // Unique values for filters
  uniqueClients,
  uniqueBUHeads,
  uniqueLocations,
  uniqueContractTypes,
  uniqueClientProjectManagers,
  uniqueClientResourceManagers,
  uniqueReportingManagers,
  
  // Action handlers
  handleReportingInfoClick,
  handleProjectDetailsClick,
  setSelectedCandidate,
  setShowEditModal,
  setShowCandidateDetails,
  setShowDeleteModal,
  setShowRollOffEditModal,
  setCurrentPage,
  fetchCandidates,
  setShowAddModal,
  setShowBulkEmailPanel,
  setShowAccountManagerModal,
  getRecentCandidates,
  setRecentCandidates,
  setSelectedCandidatesForEmail,
  sendAccountManagerNotification,
  handleMarkAsInterviewPanel,
  updateCandidateInfo,

  // Modal states
  showMoreActions,
  setShowMoreActions,
  setSelectedCandidateHistory,
  fetchCandidateHistory,
  setShowHistoryModal,
  fetchCandidateNotes,
  setShowNotesModal,
  
  // Components to pass through
  MoreActionsDropdown,
  ProjectFormStatus,
  TableLoading,
  
  // New props for UnifiedModal
  API_BASE_URL={API_BASE_URL}
}) => {

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: '$',
      CAD: 'CA$',
      INR: '₹',
      GBP: '£',
      EUR: '€',
      AUD: 'A$',
      SGD: 'S$',
      MXN: 'MX$',
      BRL: 'R$',
      JPY: '¥',
      CNY: '¥',
      CHF: 'Fr',
      HKD: 'HK$',
      NZD: 'NZ$',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      ZAR: 'R',
      AED: 'د.إ',
      SAR: '﷼',
    };
    return symbols[currency?.toUpperCase()] ?? '$';
  };

  const formatDateRaw = (dateStr) => {
    if (!dateStr) return '—';
    const [year, month, day] = String(dateStr).slice(0, 10).split('-');
    return `${month}/${day}/${year}`;
  };

  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
  const [unifiedModalCandidate, setUnifiedModalCandidate] = useState(null);

  // ── Move to RD ────────────────────────────────────────────────────────────
  
const [showRDModal, setShowRDModal] = useState(false);
const [rdCandidate, setRdCandidate] = useState(null);
const [rdReason, setRdReason] = useState('');
const [rdConfirming, setRdConfirming] = useState(false);
const RD_REASONS = [
  'Resignation',
  'Project Completion',
  'FTE Conversion by Client',
  'Termination',
  'Work Authorization Expiry',
  'Budget Constraints',
  'Others',
];
const [rdOtherReason, setRdOtherReason] = useState('');
  // ── Report trigger state ───────────────────────────────────────────────────
  const [showReportDropdown, setShowReportDropdown] = useState(false);
  const [reportLoading, setReportLoading]           = useState(false);
  const [reportStatus, setReportStatus]             = useState(null);
  const [showReportPreview, setShowReportPreview]   = useState(false);
  const [previewType, setPreviewType]               = useState(null);
  const [previewBUs, setPreviewBUs]                 = useState([]);
  const [selectedBUs, setSelectedBUs]               = useState([]);
  const [previewLoading, setPreviewLoading]         = useState(false);
  const [sendingProgress, setSendingProgress]       = useState(null);
  const [sentBUs, setSentBUs]                       = useState([]);
  const [leadershipPreview, setLeadershipPreview]   = useState(null);
  const [reportHistory, setReportHistory]           = useState([]);
  const [showHistory, setShowHistory]               = useState(false);
  const reportDropdownRef                           = useRef(null);

  // Fetch report send history
  const fetchReportHistory = async () => {
    try {
      const base = API_BASE_URL || 'https://test.vdpl.co/Candidates/Candidates';
      const res  = await fetch(`${base}/cron_bu_report.php?action=history`, {
        headers: { 'Accept': 'application/json' },
      });
      const data = await res.json();
      if (data.success) setReportHistory(data.logs || []);
    } catch (err) {
      console.error('History fetch error:', err);
    }
  };

  // Close report dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(e.target)) {
        setShowReportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Open preview modal — fetch BU list first
  const openReportPreview = async (type) => {
    setShowReportDropdown(false);
    setPreviewType(type);
    setPreviewBUs([]);
    setSelectedBUs([]);
    setSendingProgress(null);
    setSentBUs([]);
    setReportStatus(null);
    setLeadershipPreview(null);
    setShowReportPreview(true);

    const base = API_BASE_URL || 'https://test.vdpl.co/Candidates/Candidates';

    if (type === 'bu' || type === 'full') {
      setPreviewLoading(true);
      try {
        const res  = await fetch(`${base}/cron_bu_report.php?action=preview`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.bus)) {
          setPreviewBUs(data.bus);
          setSelectedBUs(data.bus.map(b => b.bu_head_emailid));
        } else {
          setPreviewBUs([]);
        }
      } catch (err) {
        console.error('Preview fetch error:', err);
        setPreviewBUs([]);
      } finally {
        setPreviewLoading(false);
      }
    }

    if (type === 'all' || type === 'full') {
      setPreviewLoading(true);
      try {
        const base2 = API_BASE_URL || 'https://test.vdpl.co/Candidates/Candidates';
        const res   = await fetch(`${base2}/cron_bu_report.php?action=preview_all`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        const data = await res.json();
        if (data.success) {
          setLeadershipPreview(data);
        } else {
          console.error('Leadership preview failed:', data.message);
        }
      } catch (err) {
        console.error('Leadership preview fetch error:', err);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  // Toggle BU selection
  const toggleBUSelect = (email) => {
    setSelectedBUs(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  // Send the report
  const triggerReport = async () => {
    setSendingProgress('sending');
    const base          = API_BASE_URL || 'https://test.vdpl.co/Candidates/Candidates';
    const triggeredBy   = currentUser
      ? encodeURIComponent(`${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email || 'Unknown')
      : 'Unknown';
    const triggeredRole = encodeURIComponent(currentUser?.role || '');
    const url           = `${base}/cron_bu_report.php?trigger=${previewType}&triggered_by=${triggeredBy}&triggered_role=${triggeredRole}`;
    try {
      const res  = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      if (data.success) {
        setSentBUs(data.bus_sent || []);
        setSendingProgress('done');
        setReportStatus({ type: 'success', msg: data.message || 'Reports sent successfully.' });
        setTimeout(() => {
          setShowReportPreview(false);
          setReportStatus(null);
          setSendingProgress(null);
        }, 3000);
      } else {
        setSendingProgress(null);
        setReportStatus({ type: 'error', msg: data.message || 'Report trigger failed.' });
      }
    } catch (err) {
      setSendingProgress(null);
      setReportStatus({ type: 'error', msg: `Error: ${err.message}` });
    }
  };

  // Multi-select dropdown states
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showBUDropdown, setShowBUDropdown] = useState(false);
  const [showClientPMDropdown, setShowClientPMDropdown] = useState(false);
  const [showClientRMDropdown, setShowClientRMDropdown] = useState(false);
  const [showReportingMgrDropdown, setShowReportingMgrDropdown] = useState(false);
  const [showExtensionDropdown, setShowExtensionDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showContractTypeDropdown, setShowContractTypeDropdown] = useState(false);
  const [showCertificationDropdown, setShowCertificationDropdown] = useState(false);

  // Single-select dropdown states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showRateRangeDropdown, setShowRateRangeDropdown] = useState(false);
  const [showMarginDropdown, setShowMarginDropdown] = useState(false);
  const [showReportingStatusDropdown, setShowReportingStatusDropdown] = useState(false);
  const [showProjectStatusDropdown, setShowProjectStatusDropdown] = useState(false);
  const [showEndDateRangeDropdown, setShowEndDateRangeDropdown] = useState(false);

  // Handle opening unified modal
  const handleOpenUnifiedModal = (candidate) => {
    setUnifiedModalCandidate(candidate);
    setShowUnifiedModal(true);
  };

  // Handle saving from unified modal
  const handleSaveUnifiedModal = async (formData) => {
    if (updateCandidateInfo) {
      await updateCandidateInfo(unifiedModalCandidate.id, formData);
    }
    setShowUnifiedModal(false);
    setUnifiedModalCandidate(null);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setStartDateFilter('');
    setEndDateFilter('');
    setShowRecentOnboarded(false);
    setClientFilter([]);
    setBuHeadFilter([]);
    setClientProjectManagerFilter([]);
    setClientResourceManagerFilter([]);
    setReportingManagerFilter([]);
    setExtensionStatusFilter([]);
    setCertificationFilter([]);
    setSkillsFilter('');
    setLocationFilter([]);
    setContractTypeFilter([]);
    setRateRangeFilter('all');
    setMarginFilter('all');
    setReportingStatusFilter('all');
    setProjectStatusFilter('all');
    setEndDateRangeFilter('all');
    setCustomEndDateStart('');
    setCustomEndDateEnd('');
  };

  // Helper function for multi-select toggles
  const toggleFilter = (setFilter, value) => {
    setFilter(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
    });
  };

  // Single-select dropdown component
  const SingleSelectDropdown = ({
    label,
    value,
    onChange,
    options,
    showDropdown,
    setShowDropdown,
    placeholder = "Select..."
  }) => {
    const displayText = value === 'all' ? placeholder : value;
    
    return (
      <div className="relative" style={{ zIndex: showDropdown ? 200 : 'auto' }}>
        {label && (
          <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-blue-500"></span>
            {label}
          </label>
        )}
        
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white hover:border-slate-300 transition-colors text-left flex items-center justify-between flex-shrink-0"
        >
          <span className={value === 'all' ? 'text-slate-400' : 'text-slate-900'}>
            {displayText}
          </span>
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showDropdown && (
          <>
            <div 
              className="fixed inset-0 z-[150]" 
              onClick={() => setShowDropdown(false)}
            />
            <div className="absolute z-[200] mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-2xl animate-slideDown"
              style={{ minWidth: '200px' }}>
              <div className="p-2">
                {options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onChange(option.value);
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 rounded-md text-xs transition-colors ${
                      value === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Multi-select dropdown component
  const MultiSelectDropdown = ({
    label, 
    options, 
    selectedValues, 
    onToggle, 
    showDropdown, 
    setShowDropdown,
    placeholder = "Select..."
  }) => {
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];
    const displayText = selectedArray.length === 0 
      ? placeholder 
      : selectedArray.length === 1 
        ? selectedArray[0]
        : `${selectedArray.length} selected`;
    
    return (
      <div className="relative" style={{ zIndex: showDropdown ? 200 : 'auto' }}>
        {label && (
          <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-blue-500"></span>
            {label}
          </label>
        )}
        
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white hover:border-slate-300 transition-colors text-left flex items-center justify-between flex-shrink-0"
        >
          <span className={selectedArray.length === 0 ? 'text-slate-400' : 'text-slate-900'}>
            {displayText}
          </span>
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showDropdown && (
          <>
            <div 
              className="fixed inset-0 z-[150]" 
              onClick={() => setShowDropdown(false)}
            />
            <div className="absolute z-[200] mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-2xl animate-slideDown"
              style={{ minWidth: '200px' }}>
              <div className="p-2">
                {options.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500 text-center">
                    No options available
                  </div>
                ) : (
                  options.map((option) => (
                    <label
                      key={option}
                      className="flex items-center px-3 py-2 hover:bg-slate-50 rounded-md cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedArray.includes(option)}
                        onChange={() => onToggle(option)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-2 text-xs text-slate-700 group-hover:text-slate-900">
                        {option}
                      </span>
                    </label>
                  ))
                )}
              </div>
              {selectedArray.length > 0 && (
                <div className="border-t border-slate-200 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      onToggle(null, true);
                      setShowDropdown(false);
                    }}
                    className="w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2 px-2 sm:px-4 lg:px-0">

      {/* ── Sticky Top Bar ─────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm -mx-2 sm:-mx-4 lg:mx-0 px-2 sm:px-4 lg:px-2 py-3">
        <div className="flex items-center justify-between gap-3">

          {/* Left Section - Title, Count & Quick Actions */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">Candidates</h3>
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-500">{filteredCandidates.length} total</p>
              </div>
            </div>

            {/* Notes & Reminder Buttons */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowNotesPanel(true)}
                className="group relative p-2 bg-gradient-to-br from-green-50 to-emerald-50 text-green-600 rounded-xl hover:from-green-100 hover:to-emerald-100 transition-all duration-200 hover:shadow-md border border-green-200/50"
                title="Add Note"
              >
                <FileText className="w-4 h-4 transition-transform" />
              </button>
              {/* <button 
                onClick={() => setShowReminderPanel(true)}
                className="group relative p-2 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-600 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-all duration-200 hover:shadow-md border border-amber-200/50"
                title="Set Reminder"
              >
                <Bell className="w-4 h-4 transition-transform" />
              </button> */}
            </div>
          </div>
          
          {/* Right Section - Notify AMs + Send Report + Add New */}
          <div className="flex items-center gap-2">

            {/* ── Notify AMs Button ──────────────────────────────────────── */}
            {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
              <button 
                onClick={() => {
                  const recent = getRecentCandidates();
                  setRecentCandidates(recent);
                  setSelectedCandidatesForEmail([]);
                  setShowAccountManagerModal(true);
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-white text-green-700 border border-green-200 rounded-xl hover:bg-green-50 hover:border-green-300 transition-colors text-sm font-semibold"
              >
                <Bell className="w-4 h-4" />
                <span className="hidden sm:inline">Notify AMs</span>
              </button>
            )}

            {/* ── Send Report Button ──────────────────────────────────────── */}
            {currentUser?.role !== 'Account Manager' && <div className="relative" ref={reportDropdownRef}>
              <button
                onClick={() => setShowReportDropdown(!showReportDropdown)}
                disabled={reportLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors border ${
                  reportLoading
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700'
                }`}
              >
                {reportLoading
                  ? <RefreshCw className="w-4 h-4 animate-spin" />
                  : <BarChart2 className="w-4 h-4" />
                }
                <span className="hidden sm:inline">
                  {reportLoading ? 'Sending...' : 'Send Report'}
                </span>
                {!reportLoading && (
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showReportDropdown ? 'rotate-180' : ''}`} />
                )}
              </button>

              {/* Status toast */}
              {reportStatus && (
                <div className={`absolute right-0 top-full mt-2 px-4 py-2.5 rounded-xl text-xs font-medium shadow-lg z-50 whitespace-nowrap flex items-center gap-2 ${
                  reportStatus.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {reportStatus.type === 'success'
                    ? <CheckCircle className="w-3.5 h-3.5" />
                    : <AlertCircle className="w-3.5 h-3.5" />
                  }
                  {reportStatus.msg}
                </div>
              )}

              {/* Dropdown menu */}
              {showReportDropdown && !reportLoading && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden animate-slideDown">
                  {/* Header */}
                  <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-slate-100">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                      <Send className="w-3.5 h-3.5 text-indigo-500" />
                      Trigger Report
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Send roll-off reports manually</p>
                  </div>

                  <div className="p-2">
                    {/* Individual BU Reports */}
                    <button
                      onClick={() => openReportPreview('bu')}
                      className="w-full flex items-start gap-3 px-3 py-3 hover:bg-slate-50 rounded-xl transition-colors group text-left"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                        <Users2 className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Individual BU Reports</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">Send weekly roll-off report to each BU head</p>
                      </div>
                    </button>

                    <div className="h-px bg-slate-100 mx-3 my-1" />

                    {/* Leadership / CEO Report */}
                    <button
                      onClick={() => openReportPreview('all')}
                      className="w-full flex items-start gap-3 px-3 py-3 hover:bg-slate-50 rounded-xl transition-colors group text-left"
                    >
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-200 transition-colors">
                        <BarChart2 className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Leadership Report</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">Send consolidated CEO report across all BUs</p>
                      </div>
                    </button>

                    <div className="h-px bg-slate-100 mx-3 my-1" />

                    {/* Send All */}
                    <button
                      onClick={() => openReportPreview('full')}
                      className="w-full flex items-start gap-3 px-3 py-3 hover:bg-emerald-50 rounded-xl transition-colors group text-left"
                    >
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                        <Send className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Send All Reports</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">Send BU reports + leadership report together</p>
                      </div>
                    </button>

                    <div className="h-px bg-slate-100 mx-3 my-1" />

                    {/* Send History */}
                    <button
                      onClick={() => { fetchReportHistory(); setShowHistory(true); setShowReportDropdown(false); }}
                      className="w-full flex items-start gap-3 px-3 py-3 hover:bg-slate-50 rounded-xl transition-colors group text-left"
                    >
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200 transition-colors">
                        <FileText className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Send History</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">View past report activity log</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>}

            {/* ── Add New Button ──────────────────────────────────────────── */}
            {currentUser?.role !== 'Account Manager' && <button 
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 via-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:via-blue-700 hover:to-purple-700 transition-colors text-sm font-semibold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add New</span>
              <span className="sm:hidden">Add</span>
            </button>}
          </div>
        </div>
      </div>

      {/* ── Professional Filter Container ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" style={{ overflow: 'visible' }}>
        {/* Search Section */}
        <div className="p-4 border-b border-slate-100">
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors">
              <Search className="w-5 h-5" />
            </div>
            <input
              type="text"
              placeholder="Search candidates by name, email, client, or skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-slate-50 hover:bg-white transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Quick Filters Bar */}
        <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100" style={{ overflow: 'visible' }}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Quick Filters:</span>
            
            {/* Status Dropdown */}
            <div style={{ minWidth: '140px' }}>
              <SingleSelectDropdown
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                showDropdown={showStatusDropdown}
                setShowDropdown={setShowStatusDropdown}
                placeholder="Status"
              />
            </div>

            {/* Clients Dropdown */}
            <div style={{ minWidth: '140px' }}>
              <MultiSelectDropdown
                options={uniqueClients}
                selectedValues={clientFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setClientFilter([]);
                  else toggleFilter(setClientFilter, value);
                }}
                showDropdown={showClientDropdown}
                setShowDropdown={setShowClientDropdown}
                placeholder="Clients"
              />
            </div>

            {/* Business Units Dropdown */}
            <div style={{ minWidth: '140px' }}>
              <MultiSelectDropdown
                options={uniqueBUHeads}
                selectedValues={buHeadFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setBuHeadFilter([]);
                  else toggleFilter(setBuHeadFilter, value);
                }}
                showDropdown={showBUDropdown}
                setShowDropdown={setShowBUDropdown}
                placeholder="Business Units"
              />
            </div>

            {/* Date Range Filters */}
            {!showRecentOnboarded && (
              <>
                <div className="flex items-center gap-2 ml-2">
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="date"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                      className="text-xs focus:outline-none w-[110px]"
                    />
                  </div>
                  <span className="text-slate-400 text-xs font-medium">to</span>
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="date"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                      className="text-xs focus:outline-none w-[110px]"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  showAdvancedFilters 
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30' 
                    : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Advanced</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </button>

              <button 
                onClick={fetchCandidates}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={dataLoading}
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Active Filters Pills */}
        {(searchTerm || filterStatus !== 'all' || 
          (Array.isArray(clientFilter) && clientFilter.length > 0) || 
          (Array.isArray(buHeadFilter) && buHeadFilter.length > 0) || 
          skillsFilter || 
          (Array.isArray(locationFilter) && locationFilter.length > 0) || 
          (Array.isArray(contractTypeFilter) && contractTypeFilter.length > 0) ||
          (Array.isArray(extensionStatusFilter) && extensionStatusFilter.length > 0) ||
          (Array.isArray(certificationFilter) && certificationFilter.length > 0)) && (
          <div className="px-4 py-3 bg-blue-50/30 border-b border-blue-100/50">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-slate-600 mr-1">Active:</span>
              
              {searchTerm && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-medium shadow-sm">
                  <Search className="w-3 h-3" />
                  <span className="truncate max-w-[120px]">"{searchTerm}"</span>
                  <button onClick={() => setSearchTerm('')} className="hover:text-blue-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {filterStatus !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-200 text-green-700 rounded-lg text-xs font-medium shadow-sm">
                  <CheckCircle className="w-3 h-3" />
                  {filterStatus}
                  <button onClick={() => setFilterStatus('all')} className="hover:text-green-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {Array.isArray(clientFilter) && clientFilter.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-purple-200 text-purple-700 rounded-lg text-xs font-medium shadow-sm">
                  <Building2 className="w-3 h-3" />
                  {clientFilter.length} Client{clientFilter.length > 1 ? 's' : ''}
                  <button onClick={() => setClientFilter([])} className="hover:text-purple-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {Array.isArray(buHeadFilter) && buHeadFilter.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-xs font-medium shadow-sm">
                  <Users2 className="w-3 h-3" />
                  {buHeadFilter.length} BU{buHeadFilter.length > 1 ? 's' : ''}
                  <button onClick={() => setBuHeadFilter([])} className="hover:text-indigo-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {Array.isArray(locationFilter) && locationFilter.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-teal-200 text-teal-700 rounded-lg text-xs font-medium shadow-sm">
                  <MapPin className="w-3 h-3" />
                  {locationFilter.length} Location{locationFilter.length > 1 ? 's' : ''}
                  <button onClick={() => setLocationFilter([])} className="hover:text-teal-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {Array.isArray(contractTypeFilter) && contractTypeFilter.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-pink-200 text-pink-700 rounded-lg text-xs font-medium shadow-sm">
                  <FileText className="w-3 h-3" />
                  {contractTypeFilter.length} Type{contractTypeFilter.length > 1 ? 's' : ''}
                  <button onClick={() => setContractTypeFilter([])} className="hover:text-pink-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {Array.isArray(extensionStatusFilter) && extensionStatusFilter.length > 0 && (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-200 text-amber-700 rounded-lg text-xs font-medium shadow-sm">
    <RefreshCw className="w-3 h-3" />
    {extensionStatusFilter.length} Approval{extensionStatusFilter.length > 1 ? 's' : ''}
                  <button onClick={() => setExtensionStatusFilter([])} className="hover:text-amber-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
              {Array.isArray(certificationFilter) && certificationFilter.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-yellow-200 text-yellow-700 rounded-lg text-xs font-medium shadow-sm">
                  <Award className="w-3 h-3" />
                  {certificationFilter.length} Cert{certificationFilter.length > 1 ? 's' : ''}
                  <button onClick={() => setCertificationFilter([])} className="hover:text-yellow-900 ml-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}

              <button
                onClick={clearAllFilters}
                className="ml-auto text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="border-t border-slate-200 bg-slate-50/30 overflow-visible">
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
              {/* Client PM */}
              <MultiSelectDropdown
                label="Client PM"
                options={uniqueClientProjectManagers}
                selectedValues={clientProjectManagerFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setClientProjectManagerFilter([]);
                  else toggleFilter(setClientProjectManagerFilter, value);
                }}
                showDropdown={showClientPMDropdown}
                setShowDropdown={setShowClientPMDropdown}
                placeholder="All"
              />

              {/* Client RM */}
              <MultiSelectDropdown
                label="Client RM"
                options={uniqueClientResourceManagers}
                selectedValues={clientResourceManagerFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setClientResourceManagerFilter([]);
                  else toggleFilter(setClientResourceManagerFilter, value);
                }}
                showDropdown={showClientRMDropdown}
                setShowDropdown={setShowClientRMDropdown}
                placeholder="All"
              />

              {/* Reporting Manager */}
              <MultiSelectDropdown
                label="Reporting Mgr"
                options={uniqueReportingManagers}
                selectedValues={reportingManagerFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setReportingManagerFilter([]);
                  else toggleFilter(setReportingManagerFilter, value);
                }}
                showDropdown={showReportingMgrDropdown}
                setShowDropdown={setShowReportingMgrDropdown}
                placeholder="All"
              />

              {/* Extension Status */}
<MultiSelectDropdown
  label="Approval Request"
  options={['Extension Request - Yes', 'Extension Request - No', 'Pending']}
  selectedValues={extensionStatusFilter}
  onToggle={(value, clearAll) => {
    if (clearAll) setExtensionStatusFilter([]);
    else toggleFilter(setExtensionStatusFilter, value);
  }}
  showDropdown={showExtensionDropdown}
  setShowDropdown={setShowExtensionDropdown}
  placeholder="All"
/>

              {/* Skills */}
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                  Skills
                </label>
                <input
                  type="text"
                  placeholder="Search..."
                  value={skillsFilter}
                  onChange={(e) => setSkillsFilter(e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Location */}
              <MultiSelectDropdown
                label="Location"
                options={uniqueLocations}
                selectedValues={locationFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setLocationFilter([]);
                  else toggleFilter(setLocationFilter, value);
                }}
                showDropdown={showLocationDropdown}
                setShowDropdown={setShowLocationDropdown}
                placeholder="All"
              />

              {/* Contract Type */}
              <MultiSelectDropdown
                label="Contract Type"
                options={uniqueContractTypes}
                selectedValues={contractTypeFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setContractTypeFilter([]);
                  else toggleFilter(setContractTypeFilter, value);
                }}
                showDropdown={showContractTypeDropdown}
                setShowDropdown={setShowContractTypeDropdown}
                placeholder="All"
              />

              {/* Certification */}
              <MultiSelectDropdown
                label="Certifications"
                options={uniqueCertifications}
                selectedValues={certificationFilter}
                onToggle={(value, clearAll) => {
                  if (clearAll) setCertificationFilter([]);
                  else toggleFilter(setCertificationFilter, value);
                }}
                showDropdown={showCertificationDropdown}
                setShowDropdown={setShowCertificationDropdown}
                placeholder="All"
              />

              {/* Rate Range */}
              <SingleSelectDropdown
                label="Rate Range"
                value={rateRangeFilter}
                onChange={setRateRangeFilter}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'low', label: '<$100/hr' },
                  { value: 'medium', label: '$100-$200/hr' },
                  { value: 'high', label: '$200+/hr' }
                ]}
                showDropdown={showRateRangeDropdown}
                setShowDropdown={setShowRateRangeDropdown}
                placeholder="All"
              />

              {/* Margin */}
              <SingleSelectDropdown
                label="Margin"
                value={marginFilter}
                onChange={setMarginFilter}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'low', label: '<20%' },
                  { value: 'medium', label: '20-35%' },
                  { value: 'high', label: '35%+' }
                ]}
                showDropdown={showMarginDropdown}
                setShowDropdown={setShowMarginDropdown}
                placeholder="All"
              />

              {/* Reporting Status */}
              <SingleSelectDropdown
                label="Reporting"
                value={reportingStatusFilter}
                onChange={setReportingStatusFilter}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'pending', label: 'Pending' }
                ]}
                showDropdown={showReportingStatusDropdown}
                setShowDropdown={setShowReportingStatusDropdown}
                placeholder="All"
              />

              {/* Project Status */}
              <SingleSelectDropdown
                label="Project"
                value={projectStatusFilter}
                onChange={setProjectStatusFilter}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'pending', label: 'Pending' }
                ]}
                showDropdown={showProjectStatusDropdown}
                setShowDropdown={setShowProjectStatusDropdown}
                placeholder="All"
              />

              {/* Contract Ending */}
              <SingleSelectDropdown
                label="Contract End"
                value={endDateRangeFilter}
                onChange={setEndDateRangeFilter}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'ending_7', label: 'Next 7 days' },
                  { value: 'ending_30', label: 'Next 30 days' },
                  { value: 'ending_60', label: 'Next 60 days' },
                  { value: 'custom', label: 'Custom Range' }
                ]}
                showDropdown={showEndDateRangeDropdown}
                setShowDropdown={setShowEndDateRangeDropdown}
                placeholder="All"
              />
            </div>

            {/* Custom Date Range */}
            {endDateRangeFilter === 'custom' && (
              <div className="px-3 pb-3">
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={customEndDateStart}
                    onChange={(e) => setCustomEndDateStart(e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                    placeholder="Start"
                  />
                  <input
                    type="date"
                    value={customEndDateEnd}
                    onChange={(e) => setCustomEndDateEnd(e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                    placeholder="End"
                  />
                </div>
              </div>
            )}

            <div className="px-3 pb-2 flex items-center justify-between">
              <span className="text-[10px] text-slate-600">
                <span className="font-semibold text-blue-600">{filteredCandidates.length}</span> matches
              </span>
              <button
                onClick={clearAllFilters}
                className="px-2 py-1 text-[10px] text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors"
              >
                Reset all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Admin Quick Actions - Floating Pills */}
      {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
        <div className="flex items-center gap-2">
          {/* <button 
            onClick={() => {
              const recent = getRecentCandidates();
              setRecentCandidates(recent);
              setSelectedCandidatesForEmail([]);
              setShowAccountManagerModal(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-xs font-medium"
          >
            <Bell className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Notify AMs</span>
          </button> */}

          {/* <button 
            onClick={() => setShowBulkEmailPanel(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-xs font-medium"
          >
            <Mail className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Bulk Email</span>
          </button> */}
        </div>
      )}

      {/* Enhanced Reminder Panel */}
      <EnhancedReminderPanel 
        showReminderPanel={showReminderPanel}
        setShowReminderPanel={setShowReminderPanel}
        currentUser={currentUser}
      />

      <EnhancedPersonalNotesModal 
        showNotesPanel={showNotesPanel}
        setShowNotesPanel={setShowNotesPanel}
        currentUser={currentUser}
      />

      {/* Unified Candidate Modal */}
      <UnifiedCandidateModal
          isOpen={showUnifiedModal}
          onClose={() => {
            setShowUnifiedModal(false);
            setUnifiedModalCandidate(null);
          }}
          candidate={unifiedModalCandidate}
          onSave={handleSaveUnifiedModal}
          currentUser={currentUser}
          API_BASE_URL={API_BASE_URL}
          fetchCandidates={fetchCandidates}
        />

      {/* ── Candidates List ─────────────────────────────────────────────────── */}
      <div className="space-y-3 sm:space-y-4">
        {dataLoading ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 text-center border border-slate-200">
            <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-slate-400 mx-auto mb-4" />
            <p className="text-sm sm:text-base text-slate-500">Loading candidates...</p>
          </div>
        ) : paginatedCandidates.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 text-center border border-slate-200">
            <Users className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">No candidates found</h3>
            <p className="text-sm sm:text-base text-slate-500 mb-6">
              {candidates.length === 0 
                ? 'No candidates have been added yet. Import your first batch of candidates to get started.'
                : 'No candidates match your current search and filter criteria. Try adjusting your filters.'
              }
            </p>
            {candidates.length === 0 && currentUser?.role !== 'Account Manager' && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium text-sm sm:text-base"
              >
                Add First Candidate
              </button>
            )}
          </div>
        ) : (
          paginatedCandidates.map((candidate) => (
            <div key={candidate.id} className={`relative overflow-hidden rounded-xl sm:rounded-2xl shadow-sm border transition-all duration-200 ${
              candidate.status === 'Active' 
                ? 'bg-white border-slate-200 hover:shadow-lg hover:border-slate-300' 
                : 'bg-gray-100 border-gray-300 opacity-75 hover:shadow-md hover:border-gray-400'
            }`}>

              {/* Interview Panel Badge - Corner Ribbon */}
              {candidate.is_interview_panel && (
                <button
                  onClick={() => {
                    alert(`Interview Panel Candidate\nAdded: ${candidate.interview_panel_date ? new Date(candidate.interview_panel_date).toLocaleString() : 'Date not available'}\nStatus: ${candidate.interview_status || 'Pending'}`);
                  }}
                  className="absolute -top-1 -right-1 w-20 h-20 overflow-hidden group z-10"
                  title="Interview Panel Member - Click for details"
                >
                  <div className="absolute top-3 -right-1 w-28 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 transform rotate-45 translate-x-8 -translate-y-2 shadow-lg group-hover:from-purple-600 group-hover:to-indigo-700 transition-all">
                    <div className="flex items-center justify-center h-full text-white text-xs font-bold tracking-wider">
                      IPM
                    </div>
                  </div>
                </button>
              )}

              <div className="p-4 sm:p-6">
                {/* Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex flex-wrap items-center gap-2 flex-1">
                        <h3 className={`text-lg sm:text-xl font-bold ${candidate.status === 'Active' ? 'text-slate-900' : 'text-gray-600'} break-words`}>
                          {candidate.candidateName}
                        </h3>
                        
                        <span className={`px-3 py-1 text-xs sm:text-sm font-semibold rounded-full inline-block ${
  candidate.status === 'Active' 
    ? 'bg-green-100 text-green-800 border border-green-200' 
    : 'bg-slate-100 text-slate-800 border border-slate-200'
}`}>
  {candidate.status}
</span>

{/* Extension Status Badges */}
{(candidate.extension_requested === 1 || candidate.extension_requested === '1') && (
  <span className="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full bg-green-100 text-green-800 border border-green-200 inline-block">
    ✓ Extended
  </span>
)}
{(candidate.extension_requested === 2 || candidate.extension_requested === '2') && (
  <span className="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full bg-red-100 text-red-800 border border-red-200 inline-block">
    ✗ Not Extended
  </span>
)}

{/* Certifications Badge with Hover Tooltip */}
                        {candidate.certifications && candidate.certifications.length > 0 && (
                          <div className="group relative">
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 border border-amber-200 rounded-lg cursor-pointer transition-all shadow-sm">
                              <Award className="w-3.5 h-3.5 text-amber-600" />
                              <span className="text-xs font-semibold text-amber-700">
                                {candidate.certifications.length} {candidate.certifications.length === 1 ? 'Certification' : 'Certifications'}
                              </span>
                            </div>
                            
                            {/* Hover Tooltip */}
                            <div className="absolute left-0 top-full mt-2 w-80 bg-slate-900 text-white rounded-xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                              <div className="font-semibold mb-3 flex items-center gap-2 text-sm border-b border-white/20 pb-2">
                                <Award className="w-4 h-4 text-amber-400" />
                                Professional Certifications ({candidate.certifications.length})
                              </div>
                              <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                                {candidate.certifications.map((cert, index) => (
                                  <div key={index} className="flex items-center gap-2.5 p-2 hover:bg-white/10 rounded-lg transition-colors">
                                    <CertificationIcon certName={cert} />
                                    <span className="text-xs leading-relaxed">{cert}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="absolute -top-1.5 left-6 w-3 h-3 bg-slate-900 transform rotate-45"></div>
                            </div>
                          </div>
                        )}

                        {/* LinkedIn URL */}
                        {candidate.linkedinUrl && (
                          <a 
                            href={candidate.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg font-medium transition-all group"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <p className={`font-medium text-sm sm:text-base ${candidate.status === 'Active' ? 'text-slate-600' : 'text-gray-500'} break-words`}>
                      {candidate.jobTitle}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500">{candidate.plcCode} • {candidate.sapId}</p>
                    
                    {/* Completion Status Badges */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {/* Reporting Badge */}
                      <div className="relative group">
                        <span className="flex items-center px-2 py-1 bg-purple-50 border border-purple-200 rounded-lg text-xs font-medium text-purple-700 cursor-default">
                          <Users2 className="w-3 h-3 mr-1" />
                          Reporting
                          {(candidate.workEmail || candidate.reportingManagerName || candidate.reportingManagerEmail) ? (
                            <CheckCircle className="w-3.5 h-3.5 ml-1 text-green-600" />
                          ) : (
                            <X className="w-3.5 h-3.5 ml-1 text-red-500" />
                          )}
                        </span>
                        <div className="absolute bottom-full left-0 mb-2 z-50 hidden group-hover:block pointer-events-none">
                          <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                            {(candidate.workEmail || candidate.reportingManagerName || candidate.reportingManagerEmail) ? (
                              <>
                                <p className="font-semibold text-green-400 mb-1">Completed</p>
                                {candidate.reporting_updated_by && (
                                  <p>Updated by: <span className="font-medium text-white">{candidate.reporting_updated_by}</span></p>
                                )}
                                {candidate.reporting_updated_at && (
                                  <p className="text-slate-400 mt-0.5">
                                    {new Date(candidate.reporting_updated_at).toLocaleDateString('en-US', {
                                      month: 'short', day: 'numeric', year: 'numeric'
                                    })}
                                  </p>
                                )}
                                {!candidate.reporting_updated_by && (
                                  <p className="text-slate-400">No update record</p>
                                )}
                              </>
                            ) : (
                              <p className="text-red-400">Not yet filled</p>
                            )}
                            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      </div>

                      {/* Project Badge */}
                      <div className="relative group">
                        <span className="flex items-center px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs font-medium text-blue-700 cursor-default">
                          <Briefcase className="w-3 h-3 mr-1" />
                          Project
                          {(candidate.current_technologies || candidate.project_description || candidate.project_experience_rating || candidate.project_tech_stack || candidate.project_overview) ? (
                            <CheckCircle className="w-3.5 h-3.5 ml-1 text-green-600" />
                          ) : (
                            <X className="w-3.5 h-3.5 ml-1 text-red-500" />
                          )}
                        </span>
                        <div className="absolute bottom-full left-0 mb-2 z-50 hidden group-hover:block pointer-events-none">
                          <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                            {(candidate.current_technologies || candidate.project_description || candidate.project_experience_rating || candidate.project_tech_stack || candidate.project_overview) ? (
                              <>
                                <p className="font-semibold text-green-400 mb-1">Completed</p>
                                {candidate.project_updated_by && (
                                  <p>Updated by: <span className="font-medium text-white">{candidate.project_updated_by}</span></p>
                                )}
                                {candidate.project_updated_at && (
                                  <p className="text-slate-400 mt-0.5">
                                    {new Date(candidate.project_updated_at).toLocaleDateString('en-US', {
                                      month: 'short', day: 'numeric', year: 'numeric'
                                    })}
                                  </p>
                                )}
                                {!candidate.project_updated_by && (
                                  <p className="text-slate-400">No update record</p>
                                )}
                              </>
                            ) : (
                              <p className="text-red-400">Not yet filled</p>
                            )}
                            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      </div>

                      {/* Feedback Badge */}
                      <div className="relative group">
                        <span className="flex items-center px-2 py-1 bg-orange-50 border border-orange-200 rounded-lg text-xs font-medium text-orange-700 cursor-default">
                          <MessageSquare className="w-3 h-3 mr-1" />
                          Feedback
                          {(candidate.clientRating && candidate.clientFeedback) ? (
                            <CheckCircle className="w-3.5 h-3.5 ml-1 text-green-600" />
                          ) : (
                            <X className="w-3.5 h-3.5 ml-1 text-red-500" />
                          )}
                        </span>
                        <div className="absolute bottom-full left-0 mb-2 z-50 hidden group-hover:block pointer-events-none">
                          <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                            {(candidate.clientRating && candidate.clientFeedback) ? (
                              <>
                                <p className="font-semibold text-green-400 mb-1">Completed</p>
                                {candidate.feedback_updated_by && (
                                  <p>Updated by: <span className="font-medium text-white">{candidate.feedback_updated_by}</span></p>
                                )}
                                {candidate.feedback_updated_at && (
                                  <p className="text-slate-400 mt-0.5">
                                    {new Date(candidate.feedback_updated_at).toLocaleDateString('en-US', {
                                      month: 'short', day: 'numeric', year: 'numeric'
                                    })}
                                  </p>
                                )}
                                {!candidate.feedback_updated_by && (
                                  <p className="text-slate-400">No update record</p>
                                )}
                              </>
                            ) : (
                              <p className="text-red-400">Not yet filled</p>
                            )}
                            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      </div>

                      {(candidate.sent_to_rd === 1 || candidate.sent_to_rd === '1' || candidate.sent_to_rd === true || candidate.sentToRD === true) && (
  <div className="relative group">
    <button
      className="relative flex items-center bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 text-white px-2 py-1 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
    >
      <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white/20 rounded-full flex items-center justify-center mr-1.5">
        <UserCheck className="w-2 h-2 sm:w-3 sm:h-3" />
      </div>
      <span className="text-xs font-bold tracking-wide">FORWARDED TO RD</span>
    </button>

    {/* Hover Tooltip */}
    <div className="absolute bottom-full left-0 mb-2 w-64 bg-slate-900 text-white rounded-xl p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl pointer-events-none">
      <p className="text-xs font-bold text-indigo-300 mb-2 flex items-center gap-1.5">
        <UserCheck className="w-3.5 h-3.5" />
        Forwarded to RD Team
      </p>
      <div className="space-y-1.5">
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">Reason</p>
          <p className="text-xs text-white font-medium">{candidate.rd_reason || 'No reason specified'}</p>
        </div>
        {(candidate.sent_to_rd_date || candidate.sentToRDDate) && (
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Forwarded On</p>
            <p className="text-xs text-slate-300">
              {new Date(candidate.sent_to_rd_date || candidate.sentToRDDate).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric'
              })}
            </p>
          </div>
        )}
      </div>
      <div className="absolute -bottom-1.5 left-6 w-3 h-3 bg-slate-900 transform rotate-45"></div>
    </div>
  </div>
)}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
<div className="flex flex-wrap items-center gap-2 sm:gap-3">
  {/* Unified Update Button */}
  <button 
    onClick={() => handleOpenUnifiedModal(candidate)}
    className="flex items-center px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-600 rounded-xl hover:from-purple-600 hover:via-blue-600 hover:to-indigo-700 shadow-lg shadow-purple-500/30 hover:shadow-xl transition-all duration-300 hover:scale-105"
    title="Update All Information"
  >
    <Edit className="w-4 h-4 mr-2" />
    <span>Update Info</span>
  </button>

  {/* Manage / Roll-off Button — Active candidates only */}
  {candidate.status === 'Active' && (
    <button
      onClick={() => {
        setSelectedCandidate(candidate);
        setShowRollOffEditModal(true);
      }}
      className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
    >
      <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
      <span className="hidden sm:inline">Manage</span>
      <span className="sm:hidden">Roll-off</span>
    </button>
  )}

  <div className="relative z-20">   {/* MoreVertical starts here */}
                      <button 
                        onClick={() => setShowMoreActions(showMoreActions === candidate.id ? null : candidate.id)}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors relative z-20"
                      >
                        <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      {showMoreActions === candidate.id && (
                        <div className="absolute right-0 top-full mt-2 w-48 sm:w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-30">
                          <div className="py-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCandidate(candidate);
                                setShowCandidateDetails(true);
                                setShowMoreActions(null);
                              }}
                              className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                              Preview Details
                            </button>

                            {/* {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCandidate(candidate);
                                  setShowEditModal(true);
                                  setShowMoreActions(null);
                                }}
                                className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                              >
                                <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                                Edit Information
                              </button>
                            )} */}

                            <button
                              onClick={() => {
                                setSelectedCandidateHistory(candidate);
                                fetchCandidateHistory(candidate.id);
                                setShowHistoryModal(true);
                                setShowMoreActions(null);
                              }}
                              className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                              View History
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCandidate(candidate);
                                handleMarkAsInterviewPanel(candidate.id);
                                setShowMoreActions(null);
                              }}
                              className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                              Interview Panel
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCandidate(candidate);
                                fetchCandidateNotes(candidate.id);
                                setShowNotesModal(true);
                                setShowMoreActions(null);
                              }}
                              className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                              View/Add Notes {candidate.notesCount > 0 && `(${candidate.notesCount})`}
                            </button>


                            {/* ── NEW: Move to RD — only for Inactive candidates ── */}
{candidate.status !== 'Active' && (
  <>
    <hr className="my-1 border-slate-100" />
    {(candidate.sent_to_rd === 1 || candidate.sent_to_rd === '1' || candidate.sent_to_rd === true) ? (
  <div className="flex items-center w-full px-4 py-2 sm:py-3 cursor-not-allowed">
    <div className="flex items-center gap-2 w-full">
      <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
        <CheckCircle className="w-2.5 h-2.5 text-indigo-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-indigo-600">Already in RD</p>
        <p className="text-[10px] text-slate-400 truncate">
          {candidate.rd_reason || 'Forwarded to RD team'}
        </p>
      </div>
    </div>
  </div>
    ) : (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setRdCandidate(candidate);
          setRdReason('');
          setRdOtherReason('');
          setShowRDModal(true);
          setShowMoreActions(null);
        }}
        className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-amber-600 hover:bg-amber-50 transition-colors"
      >
        <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
        Move to RD
      </button>
    )}
  </>
)}

                            {/* {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                              <>
                                <hr className="my-2" />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCandidate(candidate);
                                    setShowDeleteModal(true);
                                    setShowMoreActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 sm:mr-3" />
                                  Delete Candidate
                                </button>
                              </>
                            )} */}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client & Track</p>
                    <p className="text-sm font-bold text-slate-900 break-words">{candidate.client}</p>
                    {candidate.clientTrack && (
                      <p className="text-sm text-blue-600 font-medium break-words">Track: {candidate.clientTrack}</p>
                    )}
                    <p className="text-sm text-slate-600 break-words">{candidate.projectCity}, {candidate.projectState}</p>
                    <p className="text-xs text-slate-500 break-words">{candidate.contractorStatus}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5 text-blue-600" />
                      Contact
                    </p>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-900 break-all">{candidate.candidatePhone}</p>
                      <p className="text-xs text-slate-600 truncate" title={candidate.candidateEmail}>
                        {candidate.candidateEmail}
                      </p>
                      {candidate.workEmail && (
                        <p className="text-xs text-slate-500 truncate" title={candidate.workEmail}>
                          Work: {candidate.workEmail}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contract Period</p>
                    <p className="text-sm text-slate-900">
                      {formatDateRaw(candidate.startDate)}
                    </p>
                    <p className="text-sm text-slate-600">
                      to {formatDateRaw(candidate.sowEndDate)}
                    </p>
                    {candidate.status !== 'Active' && (
                      <p className="text-xs font-medium text-red-500">
                        End: {formatDateRaw(candidate.endDate)}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      {(() => {
                        const effectiveEndDate = candidate.sowEndDate;
                        const [ey, em, ed] = String(effectiveEndDate).slice(0, 10).split('-').map(Number);
                        const endLocal = new Date(ey, em - 1, ed);
                        const todayLocal = new Date(); todayLocal.setHours(0, 0, 0, 0);
                        const days = Math.ceil((endLocal - todayLocal) / (1000 * 60 * 60 * 24));
                        return days > 0 ? `${days} days remaining` : 'Contract overdue!';
                      })()}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Financial Details</p>
                    <p className="text-sm font-bold text-slate-900">{getCurrencySymbol(candidate.payRateCurrency)}{candidate.payRate}/hr</p>
                    <p className="text-sm text-slate-600">Client: {getCurrencySymbol(candidate.clientRateCurrency)}{candidate.clientRate}/hr</p>
                    <p className="text-xs text-green-600 font-medium">Margin: {getCurrencySymbol(candidate.clientRateCurrency)}{candidate.marginValue}</p>
                  </div>
                </div>
                
                {/* Skills */}
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 mb-1.5">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Primary Skills</p>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      <ProjectFormStatus candidate={candidate} />
                      
                      {(() => {
                        const extendedCount = candidate.extended_count || candidate.extensionCount || 
                                     candidate.extension_count || candidate.extensionsCount ||
                                     (candidate.extensionStatus === 'Yes' ? 1 : null);

                        return extendedCount && extendedCount > 0 && (
                          <div className="relative group">
                            <div className="flex items-center bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white/20 rounded-full flex items-center justify-center mr-1.5 sm:mr-2">
                                <RefreshCw className="w-2 h-2 sm:w-3 sm:h-3" />
                              </div>
                              <span className="text-xs font-bold tracking-wide">EXTENDED</span>
                              <div className="ml-1.5 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5 bg-white/90 rounded-full flex items-center justify-center">
                                <span className="text-xs font-black text-amber-700">{extendedCount}</span>
                              </div>
                              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            </div>
                            
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                              Contract extended {extendedCount} time{extendedCount !== 1 ? 's' : ''}
                              <div className="absolute top-full right-4 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.primarySkills.split(',').map((skill, index) => (
                      <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[11px] rounded-full font-medium">
                        {skill.trim()}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Notes Preview */}
                {candidate.notesCount > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Notes ({candidate.notesCount})
                      </p>
                      <span className="text-xs text-slate-400">Click to view all</span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 cursor-pointer hover:text-slate-800 break-words"
                       onClick={() => {
                         setSelectedCandidate(candidate);
                         fetchCandidateNotes(candidate.id);
                         setShowNotesModal(true);
                       }}>
                      {candidate.latestNote?.note_text || 'Click to view notes...'}
                    </p>
                    {candidate.latestNote?.is_important && (
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                        Important note available
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Pagination ──────────────────────────────────────────────────────── */}
      {totalPages > 1 && !dataLoading && (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-slate-700 text-center sm:text-left">
              Showing <span className="font-semibold">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(currentPage * itemsPerPage, filteredCandidates.length)}</span> of{' '}
              <span className="font-semibold">{filteredCandidates.length}</span> candidates
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 sm:p-3 border border-slate-300 rounded-lg sm:rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-2.5 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl transition-colors ${
                      currentPage === pageNum
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'text-slate-700 hover:bg-slate-50 border border-slate-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 sm:p-3 border border-slate-300 rounded-lg sm:rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report Preview Modal — rendered via portal to escape header stacking context ── */}
      {showReportPreview && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-slideUp">

            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Send className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {previewType === 'bu'   ? 'Send BU Reports' :
                     previewType === 'all'  ? 'Send Leadership Report' :
                     'Send All Reports'}
                  </h3>
                  <p className="text-[11px] text-white/70">
                    {previewType === 'bu'   ? 'Select which BU heads to notify' :
                     previewType === 'all'  ? 'Consolidated CEO report — all BUs' :
                     'BU reports + leadership report'}
                  </p>
                </div>
              </div>
              {sendingProgress !== 'sending' && (
                <button
                  onClick={() => { setShowReportPreview(false); setSendingProgress(null); }}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              )}
            </div>

            <div className="p-6 overflow-y-auto flex-1">

              {/* ── Sending animation ─────────────────────────────────────── */}
              {sendingProgress === 'sending' && (
                <div className="flex flex-col items-center py-6 gap-4">
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-4 border-indigo-100"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Send className="w-6 h-6 text-indigo-500" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-800">Sending reports...</p>
                    <p className="text-xs text-slate-500 mt-1">Please wait, this may take a moment</p>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                  </div>
                </div>
              )}

              {/* ── Done animation ────────────────────────────────────────── */}
              {sendingProgress === 'done' && (
                <div className="flex flex-col items-center py-6 gap-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-slideUp">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-slate-800">Reports sent successfully!</p>
                    {sentBUs.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5 justify-center">
                        {sentBUs.map(bu => (
                          <span key={bu} className="px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-xs rounded-lg font-medium">
                            ✓ {bu}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">This dialog will close automatically...</p>
                </div>
              )}

              {/* ── Preview content (before sending) ─────────────────────── */}
              {!sendingProgress && (
                <>
                  {/* Leadership preview */}
                  {previewType === 'all' && (
                    <>
                      {previewLoading ? (
                        <div className="flex items-center justify-center py-8 gap-3">
                          <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
                          <span className="text-sm text-slate-500">Fetching company-wide data...</span>
                        </div>
                      ) : leadershipPreview ? (
                        <div className="space-y-4">
                          {/* Company-wide KPI cards */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-2">
                              <BarChart2 className="w-3.5 h-3.5 text-indigo-500" />
                              Company-Wide Summary — {leadershipPreview.bu_count} BU(s)
                            </p>
                            <div className="grid grid-cols-4 gap-2">
                              {[
                                { label: 'Total Roll-Off',  value: leadershipPreview.grand_rolloff,    color: '#1a237e', bg: '#f0f4ff' },
                                { label: 'Ended Planned',   value: leadershipPreview.grand_planned,    color: '#166534', bg: '#f0fff4' },
                                { label: 'Ended Early',     value: leadershipPreview.grand_early,      color: '#9a3412', bg: '#fff7ed' },
                                { label: 'Unexpected',      value: leadershipPreview.grand_unexpected, color: '#b45309', bg: '#fffbeb' },
                                { label: 'Upcoming',        value: leadershipPreview.grand_upcoming,   color: '#6b21a8', bg: '#fdf4ff' },
                                { label: 'AM Rate',         value: `${leadershipPreview.grand_am_rate}%`, color: '#374151', bg: '#f9fafb' },
                                { label: 'COI Score',       value: `${leadershipPreview.grand_coi_pct}%`, color: '#166534', bg: '#f0fff4' },
                                { label: 'Active BUs',      value: leadershipPreview.bu_count,         color: '#1e40af', bg: '#eff6ff' },
                              ].map(k => (
                                <div key={k.label} style={{ background: k.bg }} className="rounded-xl p-3 text-center border border-slate-100">
                                  <div style={{ color: k.color }} className="text-xl font-bold">{k.value}</div>
                                  <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{k.label}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* BU breakdown table */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">BU Breakdown</p>
                            <div className="rounded-xl border border-slate-200 overflow-hidden">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left px-3 py-2 font-semibold text-slate-600">BU</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">Roll-Off</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">Planned</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">Early</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">Unexpected</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">Upcoming</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">AM%</th>
                                    <th className="text-center px-2 py-2 font-semibold text-slate-600">Ext Y/N/P</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {leadershipPreview.bus.map((bu, i) => (
                                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                      <td className="px-3 py-2 font-semibold text-indigo-700">{bu.bu_head}</td>
                                      <td className="text-center px-2 py-2 font-bold text-indigo-700">{bu.rolloff_count}</td>
                                      <td className="text-center px-2 py-2 font-bold text-green-700">{bu.planned_count}</td>
                                      <td className="text-center px-2 py-2 font-bold text-orange-700">{bu.early_count}</td>
                                      <td className="text-center px-2 py-2 font-bold text-amber-700">{bu.unexpected_count}</td>
                                      <td className="text-center px-2 py-2 font-bold text-purple-700">{bu.upcoming_count}</td>
                                      <td className="text-center px-2 py-2">
                                        <span className={`font-bold ${bu.am_conn_rate >= 75 ? 'text-green-700' : bu.am_conn_rate >= 50 ? 'text-amber-700' : 'text-red-700'}`}>
                                          {bu.am_conn_rate}%
                                        </span>
                                      </td>
                                      <td className="text-center px-2 py-2">
                                        <span className="text-green-700 font-bold">{bu.ext_yes}</span>
                                        <span className="text-slate-400 mx-0.5">/</span>
                                        <span className="text-red-700 font-bold">{bu.ext_no}</span>
                                        <span className="text-slate-400 mx-0.5">/</span>
                                        <span className="text-slate-500">{bu.ext_pending}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* COI summary */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">COI Completeness — Company-Wide</p>
                            <div className="space-y-2">
                              {leadershipPreview.coi_summary.map((field, i) => (
                                <div key={i} className="flex items-center gap-3">
                                  <span className="text-xs text-slate-600 w-32 flex-shrink-0">{field.label}</span>
                                  <div className="flex-1 bg-slate-200 rounded-full h-2">
                                    <div
                                      className="h-2 rounded-full transition-all"
                                      style={{
                                        width: `${field.pct}%`,
                                        background: field.pct >= 75 ? '#166534' : field.pct >= 50 ? '#d97706' : '#b91c1c'
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs font-bold text-slate-700 w-8 text-right">{field.pct}%</span>
                                  <span className="text-[11px] text-slate-400 w-24 flex-shrink-0">
                                    {field.filled} filled / {field.missing} missing
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Upcoming per BU */}
                          {leadershipPreview.bus.some(b => b.upcoming_list?.length > 0) && (
                            <div>
                              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Upcoming Roll-Offs — Next 7 Days</p>
                              <div className="space-y-1">
                                {leadershipPreview.bus.filter(b => b.upcoming_list?.length > 0).map((bu, i) =>
                                  bu.upcoming_list.map((c, j) => (
                                    <div key={`${i}-${j}`} className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-100">
                                      <span className="text-[11px] font-semibold text-indigo-700 w-20 flex-shrink-0 truncate">{bu.bu_head}</span>
                                      <span className="text-[11px] text-slate-700 flex-1 truncate font-medium">{c.name}</span>
                                      <span className="text-[11px] text-slate-500 truncate">{c.client}</span>
                                      <span className="text-[11px] text-purple-700 font-semibold flex-shrink-0">{c.sow_end_date}</span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">No activity found this week</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* BU list with full insights */}
                  {(previewType === 'bu' || previewType === 'full') && (
                    <>
                      <p className="text-xs font-semibold text-slate-600 mb-3 flex items-center gap-2">
                        <Users2 className="w-3.5 h-3.5 text-blue-500" />
                        BUs with activity this week
                        {!previewLoading && previewBUs.length > 0 && (
                          <span className="ml-auto text-[11px] text-slate-400 font-normal">
                            {selectedBUs.length}/{previewBUs.length} selected
                          </span>
                        )}
                      </p>

                      {previewLoading ? (
                        <div className="flex items-center justify-center py-8 gap-3">
                          <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
                          <span className="text-sm text-slate-500">Fetching BU activity...</span>
                        </div>
                      ) : previewBUs.length === 0 ? (
                        <div className="text-center py-8">
                          <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">No BU activity found this week</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                          {/* Select all */}
                          <label className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors border border-slate-200">
                            <input
                              type="checkbox"
                              checked={selectedBUs.length === previewBUs.length}
                              onChange={() => {
                                if (selectedBUs.length === previewBUs.length) {
                                  setSelectedBUs([]);
                                } else {
                                  setSelectedBUs(previewBUs.map(b => b.bu_head_emailid));
                                }
                              }}
                              className="w-4 h-4 text-indigo-600 rounded border-slate-300"
                            />
                            <span className="text-xs font-semibold text-slate-700">Select All BUs</span>
                          </label>

                          {previewBUs.map(bu => (
                            <div key={bu.bu_head_emailid} className={`border rounded-xl overflow-hidden transition-all ${
                              selectedBUs.includes(bu.bu_head_emailid)
                                ? 'border-blue-200 bg-white'
                                : 'border-slate-200 bg-slate-50 opacity-60'
                            }`}>
                              {/* BU Header row with checkbox */}
                              <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50/50 transition-colors">
                                <input
                                  type="checkbox"
                                  checked={selectedBUs.includes(bu.bu_head_emailid)}
                                  onChange={() => toggleBUSelect(bu.bu_head_emailid)}
                                  className="w-4 h-4 text-indigo-600 rounded border-slate-300 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-slate-800">{bu.bu_head}</p>
                                  <p className="text-[11px] text-slate-500 truncate">{bu.bu_head_emailid}</p>
                                </div>
                                <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200 flex-shrink-0">
                                  {bu.rolloff_count} roll-off
                                </span>
                              </label>

                              {/* KPI summary cards */}
                              <div className="grid grid-cols-5 divide-x divide-slate-100 border-t border-slate-100">
                                {[
                                  { label: 'Planned',    value: bu.planned_count,    color: '#166534', bg: '#f0fff4' },
                                  { label: 'Early',      value: bu.early_count,      color: '#9a3412', bg: '#fff7ed' },
                                  { label: 'Unexpected', value: bu.unexpected_count, color: '#b45309', bg: '#fffbeb' },
                                  { label: 'Upcoming',   value: bu.upcoming_count,   color: '#6b21a8', bg: '#fdf4ff' },
                                  { label: 'AM Rate',    value: `${bu.am_conn_rate}%`, color: '#374151', bg: '#f9fafb' },
                                ].map(k => (
                                  <div key={k.label} style={{ background: k.bg }} className="text-center py-2 px-1">
                                    <div style={{ color: k.color }} className="text-sm font-bold">{k.value}</div>
                                    <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{k.label}</div>
                                  </div>
                                ))}
                              </div>

                              {/* Candidate rows */}
                              {bu.candidates && bu.candidates.length > 0 && (
                                <div className="border-t border-slate-100">
                                  {bu.candidates.map((c, i) => {
                                    const extLabel = c.ext === 1 ? 'Extended' : c.ext === 2 ? 'No Ext' : 'Pending';
                                    const extColor = c.ext === 1 ? 'text-green-700 bg-green-50 border-green-200'
                                                   : c.ext === 2 ? 'text-red-700 bg-red-50 border-red-200'
                                                   : 'text-slate-500 bg-slate-50 border-slate-200';
                                    const typeColor = c.type === 'planned' ? 'text-green-700'
                                                    : c.type === 'early'   ? 'text-orange-700'
                                                    : 'text-blue-700';
                                    return (
                                      <div key={i} className="flex items-center gap-2 px-4 py-2 border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-semibold text-slate-800 truncate">{c.name}</p>
                                          <p className="text-[11px] text-slate-500 truncate">{c.client}{c.client_track ? ` · ${c.client_track}` : ''}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0 space-y-0.5">
                                          <p className={`text-[10px] font-semibold ${typeColor}`}>{c.type}</p>
                                          <p className="text-[10px] text-slate-400">{c.sow_end_date}</p>
                                        </div>
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border flex-shrink-0 ${extColor}`}>
                                          {extLabel}
                                        </span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${c.contacted ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
                                          {c.contacted ? 'AM ✓' : 'AM ✗'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Unexpected */}
                              {bu.unexpected && bu.unexpected.length > 0 && (
                                <div className="border-t border-amber-100 bg-amber-50/30">
                                  {bu.unexpected.map((c, i) => (
                                    <div key={i} className="flex items-center gap-2 px-4 py-2 border-b border-amber-50 last:border-0">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-slate-800 truncate">{c.name}</p>
                                        <p className="text-[11px] text-slate-500 truncate">{c.client}</p>
                                      </div>
                                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded flex-shrink-0">Unexpected</span>
                                      <p className="text-[10px] text-slate-400 flex-shrink-0">{c.end_date}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* COI bar */}
                              {bu.coi && (
                                <div className="border-t border-slate-100 px-4 py-2 bg-slate-50/50">
                                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">COI Completeness</p>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                    {bu.coi.map((field, i) => (
                                      <div key={i} className="flex items-center gap-2">
                                        <span className="text-[10px] text-slate-600 w-24 flex-shrink-0">{field.label}</span>
                                        <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                                          <div
                                            className="h-1.5 rounded-full"
                                            style={{
                                              width: `${field.pct}%`,
                                              background: field.pct >= 75 ? '#166534' : field.pct >= 50 ? '#d97706' : '#b91c1c'
                                            }}
                                          />
                                        </div>
                                        <span className="text-[10px] font-semibold text-slate-600 w-6 flex-shrink-0">{field.pct}%</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {previewType === 'full' && previewBUs.length > 0 && (
                        <div className="mt-3 flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                          <BarChart2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                          <p className="text-[11px] text-indigo-700">Leadership report will also be sent after BU reports.</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Error */}
                  {reportStatus?.type === 'error' && (
                    <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <p className="text-xs text-red-700">{reportStatus.msg}</p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-3 mt-5 pt-4 border-t border-slate-100 sticky bottom-0 bg-white">
                    <button
                      onClick={() => { setShowReportPreview(false); setReportStatus(null); }}
                      className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={triggerReport}
                      disabled={previewLoading || (previewType !== 'all' && selectedBUs.length === 0)}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-xl text-sm font-semibold hover:from-indigo-600 hover:to-blue-700 transition-all shadow-lg shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Send Now
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {/* ── Report History Modal ─────────────────────────────────────────── */}
      {showHistory && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden animate-slideUp">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-700 to-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Report Send History</h3>
                  <p className="text-[11px] text-white/70">All manual report triggers</p>
                </div>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {reportHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No report history found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {reportHistory.map((log, i) => {
                    const typeLabel = log.report_type === 'bu'   ? 'BU Reports'
                                    : log.report_type === 'all'  ? 'Leadership Report'
                                    : log.report_type === 'full' ? 'All Reports'
                                    : log.report_type;
                    const typeColor = log.report_type === 'bu'   ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : log.report_type === 'all'  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 bg-white border border-slate-100 rounded-xl hover:border-slate-200 transition-colors">
                        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Send className="w-3.5 h-3.5 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${typeColor}`}>
                              {typeLabel}
                            </span>
                            <span className="text-xs font-semibold text-slate-800">{log.triggered_by}</span>
                            {log.triggered_by_role && (
                              <span className="text-[11px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                {log.triggered_by_role}
                              </span>
                            )}
                          </div>
                          {log.bus_sent && (
                            <p className="text-[11px] text-slate-500 mt-1 truncate">
                              BUs: {log.bus_sent}
                            </p>
                          )}
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {(() => {
                              try {
                                const d = new Date(log.sent_at.replace(' ', 'T'));
                                return isNaN(d.getTime()) ? log.sent_at : d.toLocaleString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                });
                              } catch {
                                return log.sent_at;
                              }
                            })()}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-xs font-bold text-slate-700">{log.sent_count}</span>
                          <p className="text-[10px] text-slate-400">sent</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-slate-100">
              <button
                onClick={() => setShowHistory(false)}
                className="w-full px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      , document.body)}

{/* ── Move to RD Confirmation Modal ─────────────────────────── */}
{showRDModal && rdCandidate && createPortal(
  <div 
  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn"
  onClick={() => {
    setShowRDModal(false);
    setRdCandidate(null);
    setRdReason('');
    setRdOtherReason('');
  }}
>
  <div 
    className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slideUp"
    onClick={(e) => e.stopPropagation()}
  >

      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center gap-3">
        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">Move to RD Team</h3>
          <p className="text-[11px] text-white/75">This action will forward the candidate</p>
        </div>
      </div>

      <div className="p-6">
  <div className="flex gap-6">

    {/* Left Column — Caution + Candidate Summary */}
    <div className="flex-1 space-y-4">

      {/* Caution Banner */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-bold text-amber-800 mb-1">Caution — Irreversible Action</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            You are about to forward <span className="font-semibold">{rdCandidate.candidateName}</span> to the RD team.
            Once moved, the candidate will be marked as <span className="font-semibold">"Forwarded to RD"</span> and
            the RD team and Account Managers will be notified. Please select a reason before proceeding.
          </p>
        </div>
      </div>

      {/* Candidate Summary */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">{rdCandidate.candidateName}</p>
          <p className="text-xs text-slate-500 truncate">{rdCandidate.jobTitle} — {rdCandidate.client}</p>
        </div>
        <span className={`ml-auto text-[11px] font-semibold px-2 py-1 rounded-lg flex-shrink-0 ${
          rdCandidate.status === 'Active'
            ? 'bg-green-100 text-green-700 border border-green-200'
            : 'bg-slate-100 text-slate-600 border border-slate-200'
        }`}>
          {rdCandidate.status}
        </span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={() => {
            setShowRDModal(false);
            setRdCandidate(null);
            setRdReason('');
            setRdOtherReason('');
          }}
          disabled={rdConfirming}
          className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            if (!rdReason) return;
            setRdConfirming(true);
            try {
              const finalReason = rdReason === 'Others' && rdOtherReason.trim()
  ? `Others - ${rdOtherReason.trim()}`
  : rdReason;
await updateCandidateInfo(rdCandidate.id, { sent_to_rd: 1, rd_reason: finalReason });
              await fetchCandidates();
            } finally {
              setRdConfirming(false);
              setShowRDModal(false);
              setRdCandidate(null);
              setRdReason('');
              setRdOtherReason('');
            }
          }}
          disabled={!rdReason || rdConfirming || (rdReason === 'Others' && !rdOtherReason.trim())}
          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {rdConfirming
            ? <><RefreshCw className="w-4 h-4 animate-spin" /> Moving...</>
            : <><UserCheck className="w-4 h-4" /> Confirm Move</>
          }
        </button>
      </div>
    </div>

    {/* Divider */}
    <div className="w-px bg-slate-200 self-stretch" />

    {/* Right Column — Reason Selection */}
    <div className="flex-1">
      <label className="text-xs font-bold text-slate-700 mb-3 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
        Select Reason <span className="text-red-500">*</span>
      </label>
      <div className="space-y-2">
  {RD_REASONS.map((reason) => (
    <label
      key={reason}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
        rdReason === reason
          ? 'border-amber-400 bg-amber-50'
          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <input
        type="radio"
        name="rdReason"
        value={reason}
        checked={rdReason === reason}
        onChange={() => {
          setRdReason(reason);
          setRdOtherReason('');
        }}
        className="w-3.5 h-3.5 text-amber-500 border-slate-300 focus:ring-amber-400"
      />
      <span className="text-xs text-slate-700">{reason}</span>
    </label>
  ))}

  {/* Others text input */}
  {rdReason === 'Others' && (
    <div className="mt-2">
      <input
        type="text"
        value={rdOtherReason}
        onChange={(e) => setRdOtherReason(e.target.value)}
        placeholder="Please specify the reason..."
        className="w-full px-3 py-2.5 border border-amber-300 bg-amber-50 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 placeholder:text-slate-400"
      />
    </div>
  )}
</div>
    </div>

  </div>
</div>
    </div>
  </div>
, document.body)}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        .animate-slideDown { animation: slideDown 0.2s ease-out; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.5); }
      `}</style>
    </div>
  );
};

export default CandidatesTab;